"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, UserCheck, Scale, FileText, ArrowRight, Video } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const advisoryPillars = [{
  id: 1,
  title: "Especialistas senior al frente",
  description: "Cada sesión es conducida exclusivamente por contadores certificados, asesores fiscales y abogados corporativos.",
  icon: UserCheck
}, {
  id: 2,
  title: "Sin respuestas automatizadas",
  description: "Evaluación jurídica y fiscal individualizada. Analizamos expedientes, libros contables y normatividad aplicable en tiempo real.",
  icon: ShieldCheck
}, {
  id: 3,
  title: "Rigor y validez institucional",
  description: "Estrategias coordinadas bajo el estándar multidisciplinario de la firma: gobierno, empresa y jurisprudencia.",
  icon: Scale
}, {
  id: 4,
  title: "Minuta y conclusiones formales",
  description: "Al término de cada reunión técnica, entregamos un resumen ejecutivo con la ruta de cumplimiento y acciones recomendadas.",
  icon: FileText
}];
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="consulta-profesional-en-línea" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Briefing and Key Principles */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: "-80px"
        }} className="lg:col-span-7 flex flex-col">
            {/* Eyebrow badge */}
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 mb-4">
              <span className="px-3 py-1 text-xs font-serif uppercase tracking-widest font-bold bg-accent text-accent-foreground border border-chart-2/40 rounded-sm">
                Atención directa · Sin intermediarios
              </span>
            </motion.div>

            {/* Verbatim Headline */}
            <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold uppercase tracking-wide leading-tight text-foreground">
              Consulta profesional en línea
            </motion.h2>

            {/* Gold Hairline Motif */}
            <motion.div variants={itemVariants} className="w-12 h-[2px] bg-chart-2 my-6" />

            {/* Supporting Copy */}
            <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
              La consultoría digital de ASV Consultores traslada el rigor del despacho corporativo a un entorno remoto seguro. Todas las reuniones son dirigidas personalmente por socios y consultores especializados en materia contable, fiscal, administrativa y jurídica, garantizando dictámenes certeros sin algoritmos ni respuestas genéricas.
            </motion.p>

            {/* Structured Value Points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
              {advisoryPillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <motion.div key={pillar.id} data-index={i} variants={itemVariants} className="bg-card text-card-foreground p-5 rounded-sm border border-border border-t-2 border-t-chart-2 flex flex-col justify-between">
                    <div>
                      <div className="w-9 h-9 rounded-sm border border-chart-2/50 flex items-center justify-center bg-background text-chart-2 mb-3">
                        <IconComponent className="w-5 h-5 text-chart-2" />
                      </div>
                      <h3 className="font-serif text-sm font-semibold uppercase tracking-wider text-foreground mb-2">
                        {pillar.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-sans">
                        {pillar.description}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>

            {/* Action Triggers */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-10 pt-4">
              <Button asChild className="bg-primary text-primary-foreground border border-chart-2 rounded-sm uppercase font-serif text-xs font-semibold tracking-wider px-7 py-6 hover:bg-chart-2 hover:text-primary transition-colors">
                <Link to="/ContactPage">
                  Solicitar asesoría en línea
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-primary text-primary rounded-sm uppercase font-serif text-xs font-semibold tracking-wider px-7 py-6 hover:bg-muted transition-colors">
                <Link to="/AboutUsPage">
                  Conocer al equipo senior
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Column: High-fidelity Institutional Consultation Photography */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-60px"
        }} transition={{
          duration: 0.55,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col">
            <div className="relative rounded-sm border border-border overflow-hidden bg-muted shadow-lg">
              {/* Photo representation of formal video consultation */}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/asv_online_consultation_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/asv_online_consultation_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/asv_online_consultation_session.png-wvc-sizes" alt="Socio consultor de ASV Consultores en sesión de asesoría virtual con cliente corporativo" loading="lazy" decoding="async" className="w-full h-auto aspect-4/5 object-cover object-center" />

              {/* Status Header Badge on Image */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                <div className="inline-flex items-center gap-2 bg-primary/90 backdrop-blur-sm border border-chart-2/40 px-3 py-1.5 rounded-sm">
                  <span className="w-2 h-2 rounded-full bg-chart-2 animate-pulse" />
                  <span className="text-[11px] uppercase tracking-wider font-serif font-bold text-primary-foreground">
                    Enlace institucional cifrado
                  </span>
                </div>
                <div className="w-8 h-8 rounded-sm bg-primary/90 border border-border flex items-center justify-center text-primary-foreground">
                  <Video className="w-4 h-4 text-chart-2" />
                </div>
              </div>

              {/* Bottom Institutional Annotation Card */}
              <div className="absolute bottom-4 inset-x-4 bg-background/95 backdrop-blur-md p-4 rounded-sm border border-border shadow-sm">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-serif uppercase tracking-widest text-muted-foreground font-semibold">
                    Supervisión Directiva
                  </span>
                  <span className="text-xs font-serif uppercase tracking-wider text-chart-2 font-bold">
                    ASV Firma
                  </span>
                </div>
                <p className="text-sm font-semibold text-foreground font-sans">
                  L.C.P. Jesús Adán Avilez Soto
                </p>
                <p className="text-xs text-muted-foreground font-sans mt-0.5">
                  Director General · Gestión fiscal, gubernamental y corporativa
                </p>
                <div className="mt-3 pt-2.5 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-sans">Plataforma corporativa segura</span>
                  <span className="font-sans font-medium text-foreground">Dictamen técnico</span>
                </div>
              </div>
            </div>

            {/* Quick Context Dossier Below Image */}
            <div className="mt-5 p-4 rounded-sm border border-border bg-card">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-chart-2 mt-1.5 shrink-0" />
                <p className="text-xs text-muted-foreground leading-normal font-sans">
                  <strong className="text-foreground font-semibold">Compromiso de reserva profesional:</strong> Todas las consultas en línea se rigen bajo estricta confidencialidad fiduciaria, apego a normatividad CONAC y cumplimiento fiscal aplicable.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}